import { Label } from '@/components/ui/label';
import { SoftCard } from '@/components/ui/soft-card';
import { useToast } from '@/components/ui/toast';
import { useChannelActions } from '@/hooks/useChannelActions';

import { MultipleEmailsInput } from './MultipleEmailsInput';

const TWIN_CHANNEL_TYPES = ['product-reservations', 'local-checkout'];

/**
 * Shared "Notification email addresses" card for both Local Checkout and
 * Product Reservations. Writes the new list to whichever of the twin channel
 * records exist on the shop, mirroring the retailers-dashboard behaviour
 * (`ReservationEmailForm` writes to both regardless of whether the twin is
 * enabled), so a retailer never has to re-enter the same emails on the
 * second channel.
 *
 * Persistence is a single PATCH per channel record per change — no debounce
 * needed since chip add/remove is already a discrete user action.
 */
export function NotificationEmailsCard({ shopId, channels, value }) {
	const { update } = useChannelActions(shopId);
	const { toast } = useToast();

	const handleCommit = async (nextEmails) => {
		const writes = TWIN_CHANNEL_TYPES.filter((type) => channels.some((c) => c.type === type)).map((type) =>
			update(type, { config: { notificationEmails: nextEmails } })
		);
		try {
			await Promise.all(writes);
		} catch (err) {
			toast({
				title: 'Could not save notification emails',
				description: err?.message ?? 'Please try again.',
				variant: 'destructive'
			});
		}
	};

	return (
		<SoftCard className="space-y-3 !p-5 md:!p-6">
			<div>
				<Label htmlFor="notification-emails" className="text-sm font-bold text-foreground">
					Notification email addresses
				</Label>
				<p className="mt-1 text-xs text-muted-foreground">
					We'll email these addresses whenever a customer places, confirms or cancels an order. Add as many addresses as you like
					— they're shared between Product Reservations and Local Checkout.
				</p>
			</div>
			<MultipleEmailsInput id="notification-emails" value={value} onCommit={handleCommit} />
		</SoftCard>
	);
}
